/**
 * Supervisor Dashboard — Premium redesign of the live-call coaching center.
 *
 * Endpoints, permissions, and testids are preserved 1:1 from the prior
 * implementation. Only the visual layer changed: bright neutral surfaces,
 * soft ambient gradients (CSS-only, GPU-composited), glassmorphism KPI row,
 * tinted mode cards (Listen / Whisper / Barge), premium coach chips per row,
 * and a session-timeline panel for post-shift review.
 *
 * Zero Three.js / WebGL / canvas. Every animation respects
 * `prefers-reduced-motion`.
 *
 * Endpoints consumed (unchanged):
 *   GET   /coaching/live-calls
 *   GET   /coaching/sessions?range=week&limit=20
 *   POST  /coaching/sessions              {call_id, mode}
 *   PATCH /coaching/sessions/{id}/mode    {mode}
 *   POST  /coaching/sessions/{id}/stop
 *   POST  /coaching/sessions/{id}/react   {type}
 */

import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";
import {
  Headphones, MicrophoneSlash, Megaphone, Phone, Stop, Warning,
  ArrowsClockwise, ClockCounterClockwise, Users, ChartLineUp, Broadcast,
  ThumbsUp, Heart, Fire, HandsClapping, LockKey,
} from "@phosphor-icons/react";
import "./dashboard-premium.css";

const MODES = [
  { key: "listen",  label: "Listen",  Icon: Headphones,      variant: "listen",  help: "Hear both sides silently. Neither the agent nor the customer knows you're on the line." },
  { key: "whisper", label: "Whisper", Icon: MicrophoneSlash, variant: "whisper", help: "Speak only to the agent. The customer cannot hear anything you say." },
  { key: "barge",   label: "Barge",   Icon: Megaphone,       variant: "barge",   help: "Join the call live — both the agent and the customer hear you." },
];

const REACTIONS = [
  { key: "thumbs_up", label: "Nice",     Icon: ThumbsUp,      emoji: "👍" },
  { key: "heart",     label: "Loved it", Icon: Heart,         emoji: "❤️" },
  { key: "fire",      label: "On fire",  Icon: Fire,          emoji: "🔥" },
  { key: "clap",      label: "Bravo",    Icon: HandsClapping, emoji: "👏" },
  { key: "warning",   label: "Heads up", Icon: Warning,       emoji: "⚠️" },
];

const fmtDur = (s) => {
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60;
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};
const timeAgo = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

export default function Coaching() {
  const { user } = useAuth();
  // Whisper + Barge require the coach to actively be a party on the call —
  // that needs their own outbound line. Admins without a seat can still
  // Listen (passive), and the whole page continues to show live-monitoring
  // data. Owner (platform staff) is exempt.
  const requiresSeatFor = (mode) => (mode === "whisper" || mode === "barge");
  const modeLocked = (mode) => {
    if (!user || user.role === "owner") return false;
    if (user.role === "admin" && user.has_own_seat === false) return requiresSeatFor(mode);
    return false;
  };
  const [live, setLive] = useState([]);
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null); // current coaching session
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const reload = async () => {
    try {
      const [l, h] = await Promise.all([
        api.get("/coaching/live-calls"),
        api.get("/coaching/sessions?range=week&limit=20"),
      ]);
      setLive(l.data.live_calls || []);
      setHistory(h.data.sessions || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => {
    reload();
    timer.current = setInterval(reload, 5000);
    return () => clearInterval(timer.current);
  }, []);

  const startCoaching = async (call, mode) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/coaching/sessions", { call_id: call.id, mode });
      setActive(data);
      if (data.warning) setErr(`Session created with caveat: ${data.warning}`);
      try {
        const dev = window.__softphone;
        if (dev?.connect) {
          await dev.connect({ params: { To: `coach:${data.conference_name}:${mode}`, coachee_sid: data.agent_call_sid || "" } });
        }
      } catch (e) { console.debug("[coaching] softphone connect failed (ignored):", e); }
      reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const changeMode = async (mode) => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/coaching/sessions/${active.id}/mode`, { mode });
      setActive((a) => ({ ...a, mode: data.mode }));
      try {
        const dev = window.__softphone;
        if (dev?.disconnectAll) dev.disconnectAll();
        if (dev?.connect) await dev.connect({ params: { To: data.rejoin_uri } });
      } catch (e) { console.debug("[coaching] softphone rejoin failed (ignored):", e); }
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const stopCoaching = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.post(`/coaching/sessions/${active.id}/stop`);
      try { window.__softphone?.disconnectAll?.(); } catch (e) { console.debug("[coaching] softphone disconnect failed (ignored):", e); }
      setActive(null);
      reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const sendReaction = async (type) => {
    if (!active) return;
    try {
      await api.post(`/coaching/sessions/${active.id}/react`, { type });
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // Derived KPIs — all from data we already have.
  const kpis = useMemo(() => {
    const uniqueAgents = new Set(live.map((c) => c.agent_id || c.agent_name)).size;
    const nowMs = Date.now();
    const past7d = history.filter((s) => nowMs - new Date(s.started_at).getTime() < 7 * 86400_000);
    const past24h = history.filter((s) => nowMs - new Date(s.started_at).getTime() < 86400_000);
    const reactions7d = past7d.reduce((sum, s) => sum + (s.events || []).filter((e) => e.type === "reaction").length, 0);
    return {
      liveCount: live.length,
      agentsOnCall: uniqueAgents,
      sessions7d: past7d.length,
      sessions24h: past24h.length,
      reactions7d,
    };
  }, [live, history]);

  return (
    <div className="hbf-supervisor-dash" data-testid="coaching-page">
      {/* ── Hero ─────────────────────────────────────────────────── */}
      <header className="hd-hero" data-testid="supervisor-hero">
        <div>
          <div className="hd-hero__eyebrow" data-testid="supervisor-eyebrow">
            Live · Supervisor Control Center
          </div>
          <h1 className="hd-hero__title" data-testid="supervisor-title">
            <span className="hd-hero__title-accent">Coach</span> live calls in real time
          </h1>
          <p className="hd-hero__sub" data-testid="supervisor-subtitle">
            Listen, whisper to your agent, or barge into any live conversation.
            Every session is org-scoped and audit-logged. This page refreshes every 5 seconds.
          </p>
        </div>
        <div className="hd-hero__actions">
          <button onClick={reload} className="hd-btn hd-btn--secondary" data-testid="supervisor-refresh">
            <ArrowsClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
      </header>

      {err && (
        <div className="hd-notice hd-notice--danger" data-testid="coach-err">
          <Warning size={18} weight="bold" />
          <div>{err}</div>
        </div>
      )}

      {active && (
        <ActiveSessionBar
          session={active}
          onMode={changeMode}
          onStop={stopCoaching}
          onReact={sendReaction}
          busy={busy}
        />
      )}

      {/* ── KPI row ──────────────────────────────────────────────── */}
      <div className="hd-grid hd-grid--kpi" data-testid="supervisor-kpi-row">
        <KpiCard icon={<Broadcast size={20} weight="fill" />} label="Live calls" value={kpis.liveCount}
                 hint={kpis.liveCount === 0 ? "No active calls right now" : `${kpis.liveCount} conversation${kpis.liveCount === 1 ? "" : "s"} bridged`}
                 testId="kpi-live-calls"/>
        <KpiCard icon={<Users size={20} weight="fill" />} label="Agents on call" value={kpis.agentsOnCall}
                 variant="success"
                 hint="Distinct agents currently connected"
                 testId="kpi-agents-on-call"/>
        <KpiCard icon={<Headphones size={20} weight="fill" />} label="Coaching · 7d" value={kpis.sessions7d}
                 variant="accent"
                 hint={`${kpis.sessions24h} in the last 24 h`}
                 testId="kpi-coaching-7d"/>
        <KpiCard icon={<ChartLineUp size={20} weight="fill" />} label="Reactions · 7d" value={kpis.reactions7d}
                 variant="warning"
                 hint="Silent coach reactions delivered"
                 testId="kpi-reactions-7d"/>
      </div>

      {/* ── Mode explainers (bright, discoverable) ─────────────── */}
      <section className="hd-grid" style={{ marginTop: 28 }} data-testid="coaching-modes-triad">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em" }}>Coaching modes</h2>
          <div className="hd-panel__eyebrow">/ AUDIT-LOGGED · ORG-SCOPED</div>
        </div>
        <div className="hd-mode-grid">
          {MODES.map((m) => {
            const isActive = active?.mode === m.key;
            const locked = modeLocked(m.key);
            return (
              <div key={m.key}
                   className={`hd-mode-card hd-mode-card--${m.variant} ${isActive ? "hd-mode-card--active" : ""} ${locked ? "hd-mode-card--locked" : ""}`}
                   data-testid={`coaching-mode-idle-${m.key}`}
                   style={locked ? { opacity: 0.6, pointerEvents: "none" } : undefined}>
                <div className="hd-mode-card__head">
                  <div className="hd-mode-card__icon">{locked ? <LockKey size={22} weight="fill" /> : <m.Icon size={22} weight="fill" />}</div>
                  <div>
                    <div className="hd-mode-card__title">{m.label}</div>
                    <div className="hd-panel__eyebrow" style={{ margin: 0 }}>MODE · {m.key.toUpperCase()}</div>
                  </div>
                </div>
                <div className="hd-mode-card__desc">{m.help}</div>
                <div className="hd-mode-card__badge">
                  {locked
                    ? "Locked — needs your own seat"
                    : isActive ? "Active"
                    : live.length === 0 ? "Idle · no live calls"
                    : "Available · pick a call below"}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Live calls panel ─────────────────────────────────────── */}
      <section className="hd-card" style={{ marginTop: 28 }} data-testid="live-calls-card">
        <div className="hd-panel__head">
          <div>
            <div className="hd-panel__title">Agents currently on calls</div>
            <div className="hd-panel__eyebrow">/ LIVE · REFRESHES EVERY 5s</div>
          </div>
          <button onClick={reload} className="hd-panel__link" data-testid="refresh-live">
            <ArrowsClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>

        {live.length === 0 ? (
          <div className="hd-empty" data-testid="no-live-calls">
            <div className="hd-empty__icon"><Phone size={22} weight="fill" /></div>
            <div style={{ fontWeight: 600, color: "var(--hd-text)" }}>No live calls in your org right now</div>
            <div style={{ marginTop: 6 }}>Modes light up per row the moment an agent goes on a call.</div>
          </div>
        ) : (
          <DataTable
            testId="live-table"
            data={live}
            rowKey={(c) => c.id}
            rowTestId={(c) => `live-${c.id}`}
            searchPlaceholder="Search live calls…"
            searchKeys={["agent_name", "customer_number", "status", "direction"]}
            pageSize={20}
            columns={[
              { key: "agent_name", label: "Agent", sortable: true, render: (c) => (
                <div>
                  <div className="hd-agent-name">{c.agent_name}</div>
                  <div className="hd-agent-status">on call · {fmtDur(c.duration_seconds || 0)}</div>
                </div>
              )},
              { key: "direction", label: "Direction", sortable: true, width: "120px", render: (c) => (
                <span className={`hd-pill hd-pill--${(c.direction || "").toLowerCase() === "inbound" ? "inbound" : "outbound"}`}>
                  {c.direction || "—"}
                </span>
              )},
              { key: "customer_number", label: "Customer", sortable: true, width: "180px", render: (c) => (
                <span className="mono" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: 12, color: "var(--hd-text-muted)" }}>
                  {c.customer_number || "—"}
                </span>
              )},
              { key: "status", label: "Status", sortable: true, width: "130px", render: (c) => (
                <span className="hd-pill hd-pill--inprogress">{c.status}</span>
              )},
              { key: "_coach", label: "Coach", width: "300px", align: "right", render: (c) => (
                <div className="hd-coach-chips">
                  {MODES.map((m) => {
                    const locked = modeLocked(m.key);
                    return (
                      <button
                        key={m.key}
                        onClick={() => !locked && startCoaching(c, m.key)}
                        disabled={busy || !!active || locked}
                        title={locked ? "Locked — get your own Communication Seat to whisper or barge into calls." : m.help}
                        className={`hd-coach-chip hd-coach-chip--${m.variant}`}
                        data-testid={`start-${m.key}-${c.id}`}
                      >
                        {locked ? <LockKey size={12} weight="fill" /> : <m.Icon size={13} weight="fill" />} {m.label}
                      </button>
                    );
                  })}
                </div>
              )},
            ]}
          />
        )}

        {live.some((c) => !c.twilio_call_sid) && (
          <div className="hd-notice" data-testid="warn-no-sid" style={{ margin: "0 22px 22px" }}>
            <Warning size={18} weight="bold" />
            <div>
              Some live calls don&apos;t have a Twilio call SID (legacy or unstaged test data).
              You can still start a coaching session — the conference is created but the mid-call
              redirect is skipped.
            </div>
          </div>
        )}
      </section>

      {/* ── History timeline ─────────────────────────────────────── */}
      <section className="hd-card" style={{ marginTop: 24 }} data-testid="coaching-history-card">
        <div className="hd-panel__head">
          <div>
            <div className="hd-panel__title">Coaching history · last 7 days</div>
            <div className="hd-panel__eyebrow">/ SESSION AUDIT TRAIL</div>
          </div>
          <span className="hd-pill hd-pill--completed" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
            <ClockCounterClockwise size={12} weight="fill" /> {history.length}
          </span>
        </div>

        {history.length === 0 ? (
          <div className="hd-empty">
            <div className="hd-empty__icon"><ClockCounterClockwise size={22} weight="fill" /></div>
            <div style={{ fontWeight: 600, color: "var(--hd-text)" }}>No coaching sessions this week</div>
          </div>
        ) : (
          <DataTable
            testId="coaching-history-table"
            data={history}
            rowKey={(s) => s.id}
            searchPlaceholder="Search coaching sessions…"
            searchKeys={["supervisor_name", "supervisor_email", "agent_name", "mode", "conference_name"]}
            pageSize={20}
            columns={[
              { key: "started_at", label: "Started", sortable: true, width: "160px", render: (s) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{timeAgo(s.started_at)}</div>
                  <div style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: 11, color: "var(--hd-text-subtle)" }}>
                    {new Date(s.started_at).toLocaleString()}
                  </div>
                </div>
              )},
              { key: "supervisor_name", label: "Supervisor", sortable: true, render: (s) => s.supervisor_name || s.supervisor_email },
              { key: "agent_name",      label: "Agent",      sortable: true, render: (s) => s.agent_name || "—" },
              { key: "mode",            label: "Mode",       sortable: true, width: "120px", render: (s) => (
                <span className={`hd-pill hd-pill--${s.mode === "barge" ? "missed" : s.mode === "whisper" ? "outbound" : "inbound"}`}>{s.mode}</span>
              )},
              { key: "_hist", label: "Mode changes", width: "120px", align: "right", render: (s) => (
                <span style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: 12, color: "var(--hd-text-muted)" }}>
                  {(s.history || []).length}
                </span>
              )},
              { key: "ended_at", label: "Status", sortable: true, width: "150px", render: (s) => (
                s.ended_at
                  ? <span className="hd-pill hd-pill--no-answer">ended · {new Date(s.ended_at).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}</span>
                  : <span className="hd-pill hd-pill--completed">
                      <span style={{ width: 6, height: 6, borderRadius: 999, background: "#10B981", display: "inline-block", marginRight: 6 }} />
                      live
                    </span>
              )},
            ]}
          />
        )}
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function KpiCard({ icon, label, value, hint, variant, testId }) {
  return (
    <div className={`hd-card hd-card--glass hd-kpi ${variant ? `hd-kpi--${variant}` : ""}`} data-testid={testId}>
      <div className="hd-kpi__icon">{icon}</div>
      <div className="hd-kpi__label">{label}</div>
      <div className="hd-kpi__value">{value}</div>
      {hint && <div className="hd-kpi__trend">{hint}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function ActiveSessionBar({ session, onMode, onStop, onReact, busy }) {
  return (
    <div className="hd-active-bar" data-testid="active-coaching-bar">
      <div className="hd-active-bar__row">
        <div>
          <div className="hd-active-bar__title">
            <span className="hd-live-dot" aria-hidden="true" />
            Active session · <span style={{ color: "var(--hd-accent)" }}>{(session.mode || "").toUpperCase()}</span>
          </div>
          <div className="hd-active-bar__meta">
            Monitoring {session.agent_name || "agent"} · conference{" "}
            <span style={{ color: "var(--hd-text)" }}>{session.conference_name}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {MODES.map((m) => {
            const isActive = session.mode === m.key;
            return (
              <button
                key={m.key}
                onClick={() => onMode(m.key)}
                disabled={busy || isActive}
                className={`hd-btn ${isActive ? (m.key === "barge" ? "hd-btn--danger" : "hd-btn--primary") : "hd-btn--ghost"}`}
                data-testid={`mode-${m.key}`}
              >
                <m.Icon size={14} weight="fill" /> {m.label}
              </button>
            );
          })}
          <button onClick={onStop} disabled={busy} className="hd-btn hd-btn--danger" data-testid="stop-coaching">
            <Stop size={14} weight="fill" /> End session
          </button>
        </div>
      </div>
      <div className="hd-react-row">
        <span className="hd-react-row__label">/ SILENT REACTION</span>
        {REACTIONS.map((r) => (
          <button
            key={r.key}
            onClick={() => onReact(r.key)}
            disabled={busy}
            title={`Send "${r.label}" to ${session.agent_name || "the agent"} — silent, no audio.`}
            className="hd-react-btn"
            data-testid={`react-${r.key}`}
          >
            <span className="hd-react-btn__emoji">{r.emoji}</span>
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );
}
